import { useMemo, useState, type FormEvent } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { broadcast, broadcastPreview, type BroadcastAudience, type BroadcastPreview } from "./data";
import { MAX_BODY, personName, type TalentLite } from "./model";
import { t } from "../../../lib/sprache";

/**
 * Rundnachricht (Praxisfall): an alle Creator, eine Gruppe oder eine Auswahl. Jede Nachricht steht als eigene Nachricht
 * im Einzelfaden des Creators; Antworten bleiben privat. Vorschau vor dem Senden: wie viele Creator sie im Portal lesen,
 * wer sie nur per Mail bekommt (kein Zugang) und wer gar nicht erreichbar ist (bleibt außen vor). Danach ein Versandbericht.
 * Rechte wie beim Kalender: „Alle“ nur, wer alle Creator sieht; Gruppen nur, wenn alle Mitglieder sichtbar sind.
 */
export function Rundnachricht({ orgId, seesAll, talents, onDone }: {
  orgId: string;
  seesAll: boolean;
  talents: TalentLite[];
  onDone: () => void;
}) {
  const [audience, setAudience] = useState<BroadcastAudience>(seesAll ? "all" : "selection");
  const [groupId, setGroupId] = useState("");
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [bericht, setBericht] = useState<(BroadcastPreview & { sent: number }) | null>(null);
  const { busy, error, run } = useAction();
  const aktive = useMemo(() => talents.filter((t) => t.status === "active"), [talents]);
  const gruppen = useQuery({
    queryKey: ["talent_groups", orgId, "rundnachricht"],
    queryFn: async () => {
      const [g, m] = await Promise.all([
        api.select<{ id: string; name: string }>("talent_groups", { columns: "id, name", filters: [["org_id", "eq", orgId]], order: [["name", "asc"]] }),
        api.select<{ group_id: string; talent_id: string }>("talent_group_members", { columns: "group_id, talent_id", filters: [["org_id", "eq", orgId]] }),
      ]);
      const sichtbar = new Set(talents.map((t) => t.id));
      // Nur Gruppen, deren Mitglieder die Person alle sieht (sonst lehnt die Datenbank ab, Befund 22)
      return g.filter((x) => m.filter((y) => y.group_id === x.id).every((y) => sichtbar.has(y.talent_id)));
    },
  });
  const ids = [...auswahl];
  const bereit = audience === "all" || (audience === "group" && !!groupId) || (audience === "selection" && ids.length > 0);
  const vorschau = useQuery({
    queryKey: ["chat_broadcast_preview", orgId, audience, groupId, ids.sort().join(","), "rundnachricht"],
    enabled: bereit,
    placeholderData: keepPreviousData,
    queryFn: () => broadcastPreview(api, orgId, { audience, groupId: audience === "group" ? groupId : null, talentIds: audience === "selection" ? ids : undefined }),
  });
  const v = bereit ? vorschau.data : undefined;
  const erreichbar = v ? v.portal + v.mail_only : 0;

  async function senden(e: FormEvent) {
    e.preventDefault();
    if (!text.trim() || !bereit) return;
    const r = await run("rundnachricht", () => broadcast(api, orgId, {
      audience, groupId: audience === "group" ? groupId : null, talentIds: audience === "selection" ? ids : undefined, body: text.trim(),
    }));
    if (!r) return;
    setBericht(r);
    toast(t(`Rundnachricht an ${r.sent} ${r.sent === 1 ? "Creator" : "Creator"} gesendet.`, `Broadcast sent to ${r.sent} ${r.sent === 1 ? "creator" : "creators"}.`));
  }

  if (bericht) {
    return (
      <div className="stack" role="status">
        <p><strong>{t("Versendet an ", "Sent to ")}{bericht.sent} {t("Creator.", "creators.")}</strong></p>
        <ul>
          <li>{bericht.portal} {t("lesen sie im Portal (mit Chat-Sammelmail, wenn sie sie nicht lesen).", "read it in the portal (with a chat digest email if they do not read it).")}</li>
          {bericht.mail_only > 0 && <li>{bericht.mail_only} {t("ohne Portalzugang bekommen sie per Mail und antworten per Mail.", "without portal access receive it by email and reply by email.")}</li>}
          {bericht.unreachable.length > 0 && <li>{t("Nicht erreichbar und ausgelassen (kein Zugang, keine Adresse oder Chat-Mails abbestellt): ", "Not reachable and skipped (no access, no address or chat emails unsubscribed): ")}{bericht.unreachable.join(", ")}.</li>}
        </ul>
        <p className="muted">{t("Die Nachricht steht in jedem Einzelfaden; Antworten sieht nur das Team.", "The message appears in each individual thread; only the team sees replies.")}</p>
        <div className="row"><button type="button" className="primary" onClick={onDone}>{t("Fertig", "Done")}</button></div>
      </div>
    );
  }

  return (
    <form className="stack" onSubmit={senden} noValidate>
      <fieldset className="stack tk-cal-feldgruppe">
        <legend>{t("An wen?", "To whom?")}</legend>
        <div className="toolbar" role="group" aria-label={t("Empfänger", "Recipients")}>
          {seesAll && <button type="button" aria-pressed={audience === "all"} onClick={() => setAudience("all")}>{t("Alle Creator", "All creators")}</button>}
          <button type="button" aria-pressed={audience === "group"} onClick={() => setAudience("group")} disabled={!gruppen.data?.length}>{t("Eine Gruppe", "A group")}</button>
          <button type="button" aria-pressed={audience === "selection"} onClick={() => setAudience("selection")}>{t("Auswahl", "Selection")}</button>
        </div>
        {!gruppen.data?.length && <small>{t("Noch keine Gruppe, deren Mitglieder du alle betreust.", "No group yet whose members you all manage.")}</small>}
        {!seesAll && <small>{t("„Alle Creator“ können nur Personen wählen, die alle Creator sehen.", "“All creators” can only be chosen by people who see all creators.")}</small>}
        {audience === "group" && (
          <label>
            {t("Gruppe", "Group")}<select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">{t("Bitte wählen", "Please choose")}</option>
              {(gruppen.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <small>{t("Gruppen legst du im Kalender unter „Gruppen“ an.", "You create groups in the calendar under “Groups”.")}</small>
          </label>
        )}
        {audience === "selection" && (
          <div className="tk-cal-auswahl" role="group" aria-label={t("Creator auswählen", "Choose creators")}>
            {aktive.map((t) => (
              <label key={t.id} className="row">
                <input type="checkbox" checked={auswahl.has(t.id)} onChange={(e) => setAuswahl((s) => {
                  const n = new Set(s);
                  if (e.target.checked) n.add(t.id);
                  else n.delete(t.id);
                  return n;
                })} />
                {personName(t)}
              </label>
            ))}
          </div>
        )}
      </fieldset>
      <label>
        {t("Nachricht", "Message")}<textarea rows={5} value={text} maxLength={MAX_BODY} onChange={(e) => setText(e.target.value)} placeholder={t("z. B. Ab Oktober gelten neue Vorgaben für Reels …", "e.g. From October, new guidelines apply to reels …")} required />
      </label>
      {v && (
        <p className={v.unreachable.length ? "notice" : "muted"} role="status">
          {erreichbar === 1 ? t("1 Creator bekommt", "1 creator receives") : t(`${erreichbar} Creator bekommen`, `${erreichbar} creators receive`)} {t("die Nachricht: ", "the message: ")}{v.portal} {t("im Portal", "in the portal")}{v.mail_only ? t(`, ${v.mail_only} nur per Mail (kein Portalzugang)`, `, ${v.mail_only} by email only (no portal access)`) : ""}.
          {v.unreachable.length ? t(` Nicht erreichbar und ausgelassen: ${v.unreachable.join(", ")}.`, ` Not reachable and skipped: ${v.unreachable.join(", ")}.`) : ""}
        </p>
      )}
      {vorschau.error && bereit && <p className="tk-error" role="alert">{(vorschau.error as Error).message}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="submit" className="primary" disabled={busy !== null || !bereit || !text.trim() || erreichbar === 0}>
          {busy ? t("Wird gesendet …", "Sending …") : erreichbar ? t(`An ${erreichbar} Creator senden`, `Send to ${erreichbar} creators`) : t("Senden", "Send")}
        </button>
        <button type="button" onClick={onDone}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </form>
  );
}
